/**
 * TerrainForge 상태. 하이트맵·층 텍스처는 Raster 로 들고, 지오메트리와 구운 알베도는 모듈 참조에 둔다.
 * 파라미터가 바뀌면 rebuild() 가 지오메트리와 알베도를 다시 굽는다(129² + 1024² ≈ 0.3초).
 */

import { create } from 'zustand';
import * as THREE from 'three';
import type { Field, Raster } from '../core/types.ts';
import { decodeImage, rasterToCanvas } from '../core/imaging/raster.ts';
import {
  DEFAULT_TERRAIN, bakeSplatAlbedo, buildTerrainGeometry, fbmHeightField, heightFieldFromRaster, layerSampler,
  type LayerSampler, type SplatWeights, type TerrainParams,
} from '../core/terrain/heightmap.ts';

export type LayerKey = keyof SplatWeights;
export const LAYER_LABEL: Record<LayerKey, string> = { grass: '풀', dirt: '흙·모래', rock: '바위', snow: '눈' };

export interface TerrainBuilt {
  field: Field;
  geometry: THREE.BufferGeometry;
  albedo: THREE.CanvasTexture;
  material: THREE.MeshStandardMaterial;
  mesh: THREE.Mesh;
  dispose: () => void;
}

export const terrainRef: { current: TerrainBuilt | null } = { current: null };

export interface TerrainStore {
  params: TerrainParams;
  heightmap: Raster | null;
  heightmapName: string;
  heightmapUrl: string | null;
  layers: Partial<Record<LayerKey, Raster>>;
  layerUrls: Partial<Record<LayerKey, string>>;
  seed: number;
  version: number;
  busy: string | null;
  error: string | null;
  showHero: boolean;
  wireframe: boolean;
  setParam: <K extends keyof TerrainParams>(k: K, v: TerrainParams[K]) => void;
  setHeightmap: (file: File | null) => Promise<void>;
  setLayer: (k: LayerKey, file: File | null) => Promise<void>;
  setLayerUrl: (k: LayerKey, url: string, name: string) => Promise<void>;
  reseed: () => void;
  rebuild: () => void;
  toggle: (k: 'showHero' | 'wireframe') => void;
}

let timer: ReturnType<typeof setTimeout> | null = null;

export const useTerrainStore = create<TerrainStore>((set, get) => {
  const schedule = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => get().rebuild(), 120);
  };
  return {
    params: { ...DEFAULT_TERRAIN },
    heightmap: null,
    heightmapName: '',
    heightmapUrl: null,
    layers: {},
    layerUrls: {},
    seed: 7,
    version: 0,
    busy: null,
    error: null,
    showHero: true,
    wireframe: false,

    setParam(k, v) {
      set((s) => ({ params: { ...s.params, [k]: v } }));
      schedule();
    },

    async setHeightmap(file) {
      if (!file) { set({ heightmap: null, heightmapName: '', heightmapUrl: null }); schedule(); return; }
      try {
        const r = await decodeImage(file, 512);
        set({ heightmap: r, heightmapName: file.name, heightmapUrl: URL.createObjectURL(file), error: null });
        schedule();
      } catch (err) { set({ error: err instanceof Error ? err.message : String(err) }); }
    },

    async setLayer(k, file) {
      if (!file) { set((s) => { const layers = { ...s.layers }; delete layers[k]; const layerUrls = { ...s.layerUrls }; delete layerUrls[k]; return { layers, layerUrls }; }); schedule(); return; }
      try {
        const r = await decodeImage(file, 512);
        set((s) => ({ layers: { ...s.layers, [k]: r }, layerUrls: { ...s.layerUrls, [k]: URL.createObjectURL(file) }, error: null }));
        schedule();
      } catch (err) { set({ error: err instanceof Error ? err.message : String(err) }); }
    },

    async setLayerUrl(k, url, name) {
      const blob = await (await fetch(url)).blob();
      await get().setLayer(k, new File([blob], `${name}.webp`, { type: blob.type || 'image/webp' }));
    },

    reseed() { set((s) => ({ seed: (s.seed * 9301 + 49297) % 233280 })); schedule(); },

    rebuild() {
      const s = get();
      set({ busy: '지형 굽는 중' });
      try {
        const p = s.params;
        const field = s.heightmap
          ? heightFieldFromRaster(s.heightmap, p.resolution, p.smooth, p.island)
          : fbmHeightField(p.resolution, s.seed, p.island);
        const samplers: Partial<Record<LayerKey, LayerSampler>> = {};
        for (const k of Object.keys(s.layers) as LayerKey[]) samplers[k] = layerSampler(s.layers[k]!, p.tiling);
        const albedoRaster = bakeSplatAlbedo(field, p, samplers, 1024);
        const geometry = buildTerrainGeometry(field, p.size, p.heightScale);
        const albedo = new THREE.CanvasTexture(rasterToCanvas(albedoRaster));
        albedo.colorSpace = THREE.SRGBColorSpace; albedo.anisotropy = 8; albedo.needsUpdate = true;
        const material = new THREE.MeshStandardMaterial({ map: albedo, roughness: 0.95, metalness: 0 });
        material.wireframe = s.wireframe;
        const mesh = new THREE.Mesh(geometry, material);
        mesh.name = 'terrain';
        mesh.receiveShadow = true; mesh.castShadow = true;
        terrainRef.current?.dispose();
        terrainRef.current = { field, geometry, albedo, material, mesh, dispose: () => { geometry.dispose(); albedo.dispose(); material.dispose(); } };
        set((st) => ({ version: st.version + 1, busy: null, error: null }));
      } catch (err) {
        set({ busy: null, error: err instanceof Error ? err.message : String(err) });
      }
    },

    toggle(k) {
      set((s) => ({ [k]: !s[k] }) as Partial<TerrainStore>);
      if (k === 'wireframe' && terrainRef.current) terrainRef.current.material.wireframe = get().wireframe;
    },
  };
});
